export const CREDENTIAL_TYPE = {
  PAYER: "PAYER",
  PMS: "PMS",
  BANK: "BANK",
} as const;

export const CREDENTIAL_TAB = {
  PAYER: "PAYER",
  PMS: "PMS",
  BANK: "BANK",
  OPS: "OPS",
} as const;

export const OPS_CATEGORY = {
  UNSUPPORTED_PAYER: "UNSUPPORTED_PAYER",
  PAYMENT_PORTAL: "PAYMENT_PORTAL",
  EMAIL_INBOX: "EMAIL_INBOX",
  INFRA: "INFRA",
  OTHER: "OTHER",
} as const;

export type OpsCategory = (typeof OPS_CATEGORY)[keyof typeof OPS_CATEGORY];

export const OPS_CATEGORY_LABELS: Record<OpsCategory, string> = {
  UNSUPPORTED_PAYER: "Unsupported payer",
  PAYMENT_PORTAL: "Payment portal",
  EMAIL_INBOX: "Email inbox",
  INFRA: "Infra",
  OTHER: "Other",
};

export const OPS_OWNER = {
  CAMI_OPS: "CAMI_OPS",
  BENNY_OPS: "BENNY_OPS",
  OPS: "OPS",
  CX: "CX",
} as const;

export type OpsOwner = (typeof OPS_OWNER)[keyof typeof OPS_OWNER];

export const OPS_OWNER_LABELS: Record<OpsOwner, string> = {
  CAMI_OPS: "CAMI Ops",
  BENNY_OPS: "BENNY Ops",
  OPS: "OPS",
  CX: "CX",
};

export const OPS_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  INVALID: "INVALID",
  PENDING: "PENDING",
  LOCKED: "LOCKED",
} as const;

export type OpsStatus = (typeof OPS_STATUS)[keyof typeof OPS_STATUS];

export const OPS_STATUS_LABELS: Record<OpsStatus, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  INVALID: "Invalid",
  PENDING: "Credential needed",
  LOCKED: "Locked",
};

export const OPS_MFA_TYPE = {
  NONE: "NONE",
  EMAIL: "EMAIL",
  SMS: "SMS",
  AUTHENTICATOR: "AUTHENTICATOR",
} as const;

export type OpsMfaType = (typeof OPS_MFA_TYPE)[keyof typeof OPS_MFA_TYPE];

export const OPS_MFA_TYPE_LABELS: Record<OpsMfaType, string> = {
  NONE: "None",
  EMAIL: "Email",
  SMS: "Phone",
  AUTHENTICATOR: "Authenticator",
};

export function canonicalizeTarget(value: string, options: string[]): string {
  const collapsed = value.trim().replace(/\s+/g, " ");
  const match = options.find(
    (option) => option.toLowerCase() === collapsed.toLowerCase(),
  );
  return match ?? collapsed;
}

export function isSupportPayer(
  credential: Partial<UserCredentialsMessage>,
): boolean {
  return !!credential.sourceId && !credential.name;
}

export function isNewPayer(
  credential: Partial<UserCredentialsMessage>,
): boolean {
  return !!credential.name;
}

/*
  This function derives credential type from the credential object. This is temporary
  till we add credential type as an attribute from the back-end.
 */
export function typeFromCredential(
  credential: Partial<UserCredentialsMessage>,
): (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE] {
  if (isSupportPayer(credential) || isNewPayer(credential)) {
    return CREDENTIAL_TYPE.PAYER;
  }
  return CREDENTIAL_TYPE.PMS;
}

export const MFA_METHOD = {
  NONE: "NONE",
  EMAIL: "EMAIL",
  AUTHENTICATOR: "AUTHENTICATOR",
} as const;

export type MfaMethod = (typeof MFA_METHOD)[keyof typeof MFA_METHOD];

// Human-editable mfa_json keys. Every other key (machine-written session state
// such as uhc_session_cookies / browser_footprint_cookie / mfa / portal) must
// survive untouched — see mergeMfaJson.
export const MFA_EMAIL_KEY = "email-mfa";
export const AUTHENTICATOR_SETUP_KEY = "authenticator_setup_key";

function parseMfaObject(
  mfaJson: string | null | undefined,
): Record<string, unknown> {
  if (!mfaJson || mfaJson.trim() === "") {
    return {};
  }
  try {
    const parsed = JSON.parse(mfaJson);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // fall through to empty object
  }
  return {};
}

// Derive the MFA method + seed values from an existing mfa_json blob.
// Authenticator wins over email, matching the backend's _mfa_type_from.
export function parseMfaJson(mfaJson: string | null | undefined): {
  method: MfaMethod;
  email: string;
  setupKey: string;
} {
  const result = {
    method: MFA_METHOD.NONE as MfaMethod,
    email: "",
    setupKey: "",
  };
  const parsed = parseMfaObject(mfaJson);

  const setupKey = parsed[AUTHENTICATOR_SETUP_KEY];
  const email = parsed[MFA_EMAIL_KEY];

  if (typeof setupKey === "string" && setupKey.trim() !== "") {
    result.method = MFA_METHOD.AUTHENTICATOR;
    result.setupKey = setupKey;
  } else if (typeof email === "string" && email.trim() !== "") {
    result.method = MFA_METHOD.EMAIL;
    result.email = email;
  }

  return result;
}

export function normalizeSetupKey(value: string): string {
  return value.replace(/\s+/g, "").toUpperCase();
}

// Merge the chosen MFA method into an existing mfa_json blob, preserving every
// key we don't own. Returns a JSON string.
export function mergeMfaJson(
  existingMfaJson: string | null | undefined,
  method: MfaMethod,
  email: string | undefined,
  setupKey: string | undefined,
): string {
  const merged = parseMfaObject(existingMfaJson);

  if (method === MFA_METHOD.EMAIL) {
    merged[MFA_EMAIL_KEY] = (email ?? "").trim();
    delete merged[AUTHENTICATOR_SETUP_KEY];
  } else if (method === MFA_METHOD.AUTHENTICATOR) {
    merged[AUTHENTICATOR_SETUP_KEY] = normalizeSetupKey(setupKey ?? "");
    delete merged[MFA_EMAIL_KEY];
  } else {
    delete merged[MFA_EMAIL_KEY];
    delete merged[AUTHENTICATOR_SETUP_KEY];
  }

  return JSON.stringify(merged, null, 2);
}

// A key that plausibly generates a TOTP: normalized base32 of reasonable length.
export function isPlausibleSetupKey(value: string | undefined): boolean {
  const normalized = normalizeSetupKey(value ?? "");
  return /^[A-Z2-7]+=*$/.test(normalized) && normalized.length >= 16;
}

// Build a sanitized mfa_json blob for the duplicate row action: carries over
// method/email/setup key only, dropping machine-written session state.
export function mfaJsonForDuplicate(raw: string | null | undefined): {
  mfaJson: string;
  method: MfaMethod;
} {
  const parsed = parseMfaJson(raw);
  const method =
    parsed.method === MFA_METHOD.AUTHENTICATOR &&
    !isPlausibleSetupKey(parsed.setupKey)
      ? MFA_METHOD.NONE
      : parsed.method;

  return {
    mfaJson: mergeMfaJson(null, method, parsed.email, parsed.setupKey),
    method,
  };
}

// Pull the base32 secret out of an otpauth:// URI. Returns null when the value
// is not an otpauth URI or carries no secret.
export function extractOtpauthSecret(value: string): string | null {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "otpauth:") {
      return null;
    }
    return url.searchParams.get("secret");
  } catch {
    return null;
  }
}
